/**
 * SDK Initialization
 *
 * Sets up the auth interceptor to automatically add Supabase tokens
 * to all API requests when a user is logged in.
 *
 * OPTIMIZATION: Uses getSession() only (no network calls) and caches
 * the session to prevent excessive auth checks that can hit rate limits.
 */

import { client } from "./sdk/client.gen";
import { createClient as createSupabaseClient } from "@wayak/auth/utils/supabase/client";
import { requestCache } from "./utils/request-deduplication";

// Session cache to avoid repeated getSession calls
let cachedSession: { access_token: string; expires_at?: number } | null = null;
let cacheTimestamp = 0;
const CACHE_TTL = 30000; // 30 seconds

// Only set up interceptor in browser environment
if (typeof window !== "undefined") {
  // Set up request interceptor to add auth token
  client.interceptors.request.use(async (request, options) => {
    try {
      // Use request deduplication to prevent concurrent auth checks
      const session = await requestCache.deduplicate(
        "supabase-session",
        async () => {
          const now = Date.now();

          // Return cached session if still valid
          if (
            cachedSession &&
            now - cacheTimestamp < CACHE_TTL &&
            cachedSession.expires_at &&
            cachedSession.expires_at * 1000 > now
          ) {
            return cachedSession;
          }

          // Create Supabase client instance
          const supabase = createSupabaseClient();

          // Use getSession() only - it reads from local storage, no network call
          // This is much faster and doesn't hit Supabase rate limits
          const {
            data: { session },
            error,
          } = await supabase.auth.getSession();

          if (error) {
            console.debug("Auth session error:", error.message);
            // Clear cache on error
            cachedSession = null;
            cacheTimestamp = 0;
            return null;
          }

          // Cache the session
          if (session?.access_token) {
            cachedSession = {
              access_token: session.access_token,
              expires_at: session.expires_at,
            };
            cacheTimestamp = now;
            return cachedSession;
          }

          // No session available
          cachedSession = null;
          cacheTimestamp = 0;
          return null;
        },
        CACHE_TTL
      );

      // If session exists and has access token, add it to the request
      if (session?.access_token) {
        request.headers.set("Authorization", `Bearer ${session.access_token}`);
      }
    } catch (error) {
      // Silently fail if we can't get the session
      // This prevents errors in SSR or when user is not logged in
      console.debug(
        "Failed to get Supabase session for auth interceptor:",
        error
      );
    }

    return request;
  });

  // Clear cache when auth state changes
  const supabase = createSupabaseClient();
  supabase.auth.onAuthStateChange((event) => {
    if (
      event === "SIGNED_IN" ||
      event === "SIGNED_OUT" ||
      event === "TOKEN_REFRESHED"
    ) {
      // Clear cache on auth state changes
      cachedSession = null;
      cacheTimestamp = 0;
      requestCache.clear("supabase-session");
    }
  });
}
